import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTModal from '../common/RTModal';
import { apiPost, apiGet } from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import '../common/rtCommon.css';
import './GenerateMonthlyRentalInvoice.css';

const GenerateMonthlyRentalInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [currentMonth, setCurrentMonth] = useState('');
  const [currentYear, setCurrentYear] = useState('');
  const [canGenerate, setCanGenerate] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({});
  const [dateValidation, setDateValidation] = useState({ isValid: false, message: '' });

  useEffect(() => {
    const today = new Date();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    // Determine target month based on current date
    // If after 15th (> 15), generate for next month
    // If on or before 15th (<= 15), generate for current month
    const targetMonth = new Date(today);
    if (today.getDate() > 15) {
      // After 15th: generate for next month
      // Set day to 1 first to avoid date rollover (e.g., Jan 31 -> Feb 31 -> Mar 3)
      targetMonth.setDate(1);
      targetMonth.setMonth(today.getMonth() + 1);
    } else {
      // Month-start: keep current month
      targetMonth.setDate(1);
    }
    
    setCurrentMonth(monthNames[targetMonth.getMonth()]);
    setCurrentYear(targetMonth.getFullYear().toString());
    
    // Load payment settings for date validation
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const response = await apiGet('/payments/settings');
      console.log('Payment settings API response:', response);
      
      if (response && response.data) {
        setPaymentSettings(response.data);
        validateGenerationDate(response.data);
      } else {
        console.warn('No payment settings data received');
        setDefaultValidation();
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
      setDefaultValidation();
    }
  };

  const setDefaultValidation = () => {
    // Fall back to default validation if settings can't be loaded
    const today = new Date();
    const defaultValidation = today.getDate() >= 11;
    setCanGenerate(defaultValidation);
    setDateValidation({
      isValid: defaultValidation,
      message: defaultValidation ? 
        'Generation allowed (after 11th of month - default setting)' : 
        'Generation not allowed (before 11th of month - default setting)',
      startDay: 11,
      endDay: 31,
      currentDay: today.getDate(),
      rangeMessage: '11th to 31st of the month'
    });
  };

  const validateGenerationDate = (settings) => {
    const today = new Date();
    const currentDay = today.getDate();
    
    console.log('Payment settings received:', settings);
    
    // Get settings with defaults and proper parsing
    let startDay = 11; // Default
    let endDay = 31;   // Default
    
    // Parse start date setting - handle nested object structure
    if (settings.invoice_gen_start_date && settings.invoice_gen_start_date.value) {
      const parsed = parseInt(settings.invoice_gen_start_date.value, 10);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 31) {
        startDay = parsed;
      }
    }
    
    // Parse end date setting - handle nested object structure
    if (settings.invoice_gen_end_date && settings.invoice_gen_end_date.value) {
      const parsed = parseInt(settings.invoice_gen_end_date.value, 10);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 31) {
        endDay = parsed;
      }
    }
    
    console.log(`Parsed settings - startDay: ${startDay}, endDay: ${endDay}, currentDay: ${currentDay}`);
    
    // Validate current date is within allowed range
    let dateRangeValid = false;
    let rangeMessage = '';
    
    if (startDay <= endDay) {
      // Normal range (e.g., 11 to 31)
      dateRangeValid = startDay <= currentDay && currentDay <= endDay;
      rangeMessage = `${getOrdinal(startDay)} to ${getOrdinal(endDay)} of the month`;
    } else {
      // Cross-month range (e.g., 27 to 5 - from 27th of current month to 5th of next month)
      dateRangeValid = currentDay >= startDay || currentDay <= endDay;
      rangeMessage = `${getOrdinal(startDay)} of the month to ${getOrdinal(endDay)} of the next month`;
    }
    
    const currentDayText = getOrdinal(currentDay);
    
    setCanGenerate(dateRangeValid);
    setDateValidation({
      isValid: dateRangeValid,
      message: dateRangeValid ? 
        `Generation allowed (Today is ${currentDayText}, allowed range: ${rangeMessage})` :
        `Generation not allowed (Today is ${currentDayText}, allowed range: ${rangeMessage})`,
      startDay: startDay,
      endDay: endDay,
      currentDay: currentDay,
      rangeMessage: rangeMessage
    });
    
    console.log('Date validation result:', {
      dateRangeValid,
      currentDay,
      startDay,
      endDay,
      rangeMessage
    });
  };

  const getOrdinal = (day) => {
    // Ensure day is a valid number
    const num = parseInt(day, 10);
    if (isNaN(num)) {
      return `${day}th`; // Fallback for invalid numbers
    }
    
    if (num >= 11 && num <= 13) {
      return `${num}th`;
    }
    switch (num % 10) {
      case 1: return `${num}st`;
      case 2: return `${num}nd`;
      case 3: return `${num}rd`;
      default: return `${num}th`;
    }
  };



  const handleGenerateInvoices = async () => {
    if (!canGenerate) {
      if (dateValidation.rangeMessage) {
        toastError(`Monthly rental invoices can only be generated between ${dateValidation.rangeMessage}. Today is the ${getOrdinal(dateValidation.currentDay)}.`);
      } else {
        toastError('Monthly rental invoices cannot be generated at this time.');
      }
      return;
    }

    // Show toast confirmation instead of modal
    const message = `Are you sure you want to generate monthly rental invoices for ${currentMonth} ${currentYear}?`;
    const confirmed = await toastConfirm(true, message);
    
    if (confirmed) {
      await confirmGenerateInvoices();
    }
  };

  const confirmGenerateInvoices = async () => {
    
    try {
      setGenerating(true);
      
      // Determine target month based on current date
      // If after 15th (> 15), generate for next month
      // If on or before 15th (<= 15), generate for current month
      const today = new Date();
      const targetMonth = new Date(today);
      if (today.getDate() > 15) {
        // After 15th: generate for next month
        // Set day to 1 first to avoid date rollover (e.g., Jan 31 -> Feb 31 -> Mar 3)
        targetMonth.setDate(1);
        targetMonth.setMonth(today.getMonth() + 1);
      } else {
        // Month-start: keep current month
        targetMonth.setDate(1);
      }
      
      const response = await apiPost('/invoicing/generate-monthly-rental-invoices', {
        target_month: targetMonth.getMonth() + 1,
        target_year: targetMonth.getFullYear()
      });
      
      if (response && response.data) {
        setGenerationResult(response.data);
        if (response.data.status === 'success') {
          // Show modal with generation results
          setShowResultModal(true);
        } else if (response.data.generated_count === 0) {
          toastError('All allocations already have invoices for this month.');
        }
      }
    } catch (error) {
      console.error('Error generating invoices:', error);
      toastError('Unable to generate monthly rental invoices. Please try again later.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCloseModal = () => {
    setShowResultModal(false);
  };

  const handleViewInvoices = () => {
    setShowResultModal(false);
    // Navigate to view draft invoices
    navigate('/payments/view-draft-invoices');
  };

  if (loading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <RTSpinner message="Loading invoices..." />
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Generate Monthly Rental Invoice</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={() => navigate(-1)}>
              ← Back
            </RTButton>
          </div>
        </div>

        <div className="rt-main-section">
          {/* Generation Section */}
          <div className="generation-section">
            <div className="generation-header">
              <p>Generate invoices for all active paying guests for {currentMonth} {currentYear}</p>
              <p className="generation-note">📅 Invoices are generated for the next month based on configured date range</p>
              {dateValidation.rangeMessage && (
                <p className="generation-note">
                  ⚙️ Current allowed period: {dateValidation.rangeMessage} 
                  <br />
                  <small>To modify this range, go to <strong>Settings → Payment Settings</strong> and update 'invoice_gen_start_date' and 'invoice_gen_end_date'</small>
                </p>
              )}
            </div>

            <div className="generation-info">
              <div className="info-item">
                <label>Invoice Period:</label>
                <span>{currentMonth} {currentYear}</span>
              </div>
              <div className="info-item">
                <label>Generation Status:</label>
                <span className={canGenerate ? 'status-enabled' : 'status-disabled'}>
                  {dateValidation.message}
                </span>
              </div>
              {dateValidation.rangeMessage && (
                <div className="info-item">
                  <label>Allowed Date Range:</label>
                  <span className="status-info">{dateValidation.rangeMessage}</span>
                </div>
              )}
              {generationResult && (
                <div className="info-item">
                  <label>Last Generation:</label>
                  <span className={`status-${generationResult.status}`}>
                    {generationResult.message}
                  </span>
                </div>
              )}
            </div>

            <div className="generation-actions">
              <RTButton
                variant="primary"
                onClick={handleGenerateInvoices}
                disabled={!canGenerate || generating}
                loading={generating}
              >
                {generating ? 'Generating...' : 'Generate Monthly Rental Invoices'}
              </RTButton>
            </div>


          </div>

        </div>

        {/* Generation Result Modal */}
        <RTModal
          isOpen={showResultModal}
          onClose={handleCloseModal}
          title="Invoice Generation Complete"
          size="medium"
        >
          <div className="modal-content">
            <div className="generation-success">
              <div className="success-icon">✅</div>
              <h3>Successfully Generated Invoices</h3>
              
              <div className="generation-stats">
                <div className="stat-item">
                  <span className="stat-label">Month:</span>
                  <span className="stat-value">{generationResult?.month} {generationResult?.year}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Invoices Generated:</span>
                  <span className="stat-value">{generationResult?.generated_count || 0}</span>
                </div>
                {generationResult?.skipped_count > 0 && (
                  <div className="stat-item">
                    <span className="stat-label">Skipped:</span>
                    <span className="stat-value">{generationResult.skipped_count}</span>
                  </div>
                )}
              </div>

              {generationResult?.message && (
                <div className="generation-message">
                  <p>{generationResult.message}</p>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <RTButton 
                variant="secondary" 
                onClick={handleCloseModal}
              >
                Close
              </RTButton>
              <RTButton 
                variant="primary" 
                onClick={handleViewInvoices}
              >
                View Draft Invoices
              </RTButton>
            </div>
          </div>
        </RTModal>
      </div>
    </div>
  );
};

export default GenerateMonthlyRentalInvoice;
